import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import './PersonTab.css';
import { ReactComponent as MyIcon } from '../../people.svg';
import { usePersonOptions } from './usePersonOptions';
import { useSharedEvents } from '../TIMEBAR/SharedEventsContext';
import { getVideoUrl, getVideoSegment, parseTimeToSeconds, formatTimeFromSeconds} from './videoResourceService';
import EmbeddedVideoPlayer from './EmbeddedVideoPlayer';
import { openExternalVideoPlayer } from "../Tabs/externalVideoPlayer";
import EnhancedPersonSelect from "./EnhancedPersonSelect";
import './enhanced-result-card.css';
import EnhancedVideoPlayer from './EnhancedVideoPlayer';
import { useAppStore } from '../../store/appStore';

import _ from 'lodash';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// First letter capitalization helper function
const capitalizeFirst = (str) => {
  if (!str || typeof str !== 'string') return '';
  return str.charAt(0).toUpperCase() + str.slice(1);
};

/**
 * PersonTab component - Interface for person tracking and filtering
 * 
 * @param {Object} props Component properties
 * @param {Array} props.trackedObjects Array of tracked person objects
 * @returns {JSX.Element} Rendered component
 */
const PersonTab = ({ trackedObjects = [] }) => {
  // 1. State declarations
  const [trackedPerson, setTrackedPerson] = useState(null);
  const [showCalendar, setShowCalendar] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date(2025, 2, 22));
  const [trackedPersonIds, setTrackedPersonIds] = useState([]);
  const [optionsFrequency, setOptionsFrequency] = useState({});
  const [allResults, setAllResults] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [selectedTimeBlocks, setSelectedTimeBlocks] = useState(['11:00-11:30', '21:30-22:00']);
  const [selectedRobots, setSelectedRobots] = useState([]);
  const [selectedConfidenceLevels, setSelectedConfidenceLevels] = useState([]);
  const [descriptorValues, setDescriptorValues] = useState({
    hatColor: 'none',
    hairColor: 'none',
    clothingColor: 'none',
    clothingType: 'none',
    pantsColor: 'none',
    pantsType: 'none',
    shoesColor: 'none'
  });
  const [selectedBodyPart, setSelectedBodyPart] = useState(null);

  // 2. Get options from hook
  const { options: partOptions, isLoading: optionsLoading, error: optionsError, refreshOptions } = usePersonOptions();

  // 3. Define utility functions that don't depend on state or other functions
  const capitalizeFirst = useCallback((str) => {
    if (!str || typeof str !== 'string') return '';
    return str.charAt(0).toUpperCase() + str.slice(1);
  }, []);

  const getColorValue = useCallback((colorName) => {
    const colorMap = {
      'none': 'rgba(255, 255, 255, 0.29)',
      'black': '#000000',
      'white': '#ffffff',
      'blue': '#0d6efd',
      'red': '#dc3545',
      'orange': '#ffa500',
      'purple': '#9c27b0',
      'pink': '#ffd1dc',
      'green': '#198754',
      'yellow': '#ffc107',
      'gray': '#6c757d',
      'brown': '#8b4513',
      'blonde': '#f5deb3',
      'khaki': '#c3b091',
      'tan': '#d2b48c',
      'olive': '#808000',
      'light': '#f8f9fa',
      'medium': '#8a8a8a',
      'dark': '#343a40',
      'medium-dark': '#555555'
    };
    return colorMap[colorName] || 'rgba(255, 255, 255, 0.6)';
  }, []);

  // 4. Define core filtering function first
  const applyFilters = useCallback((results, filters) => {
    if (!results || !filters) return results;
    
    return results.filter(result => {
      for (const [key, value] of Object.entries(filters)) {
        if (value === 'none') continue;
        const featureValue = result.features?.[key];
        if (!featureValue || featureValue !== value) {
          return false;
        }
      }
      return true;
    });
  }, []);

  // 5. Define frequency update function that depends on applyFilters
  const updateOptionsFrequency = useCallback((results, currentFilters = null) => {
    if (!results) return;

    const frequency = {};
    const allDescriptors = [
      'hatColor', 'hairColor', 'clothingColor', 'clothingType',
      'pantsColor', 'pantsType', 'shoesColor'
    ];
    
    // 初始化所有特征的频率
    allDescriptors.forEach(key => {
      frequency[key] = {};
    });

    // 对每个特征，应用除了当前特征外的所有过滤器
    allDescriptors.forEach(descriptor => {
      // 获取应该参与过滤的结果
      let filteredResults = results;
      
      // 如果提供了当前过滤器，应用除了当前descriptor外的所有过滤器
      if (currentFilters) {
        const filtersWithoutCurrent = { ...currentFilters };
        filtersWithoutCurrent[descriptor] = 'none'; // 移除当前特征的过滤器
        
        // 应用其他过滤器
        filteredResults = applyFilters(results, filtersWithoutCurrent);
      }

      // 统计过滤后结果中当前特征的频率
      filteredResults.forEach(result => {
        const value = result.features?.[descriptor];
        if (value && value !== 'none') {
          frequency[descriptor][value] = (frequency[descriptor][value] || 0) + 1;
        }
      });
    });
    
    setOptionsFrequency(frequency);
  }, [applyFilters]);
// 6. Define data fetching function that depends on both core functions
const fetchAllData = useCallback(async () => {
  try {
    setIsSearching(true);
    const response = await fetch('${API_BASE_URL}/api/search/person');
    const data = await response.json();
    
    const formattedResults = data.map(person => formatPersonResult(person));
    setAllResults(formattedResults);
    
    const filtered = applyFilters(formattedResults, descriptorValues);
    setSearchResults(filtered);
    
    // 更新特征频率，传入当前过滤器
    updateOptionsFrequency(formattedResults, descriptorValues);
    
  } catch (error) {
    console.error('Error fetching data:', error);
    setSearchError(`Failed to fetch data: ${error.message}`);
  } finally {
    setIsSearching(false);
  }
}, [descriptorValues, applyFilters, updateOptionsFrequency]);

// 7. Define handler functions that depend on core functions
const handleBarSelect = useCallback((descriptor, value) => {
  setDescriptorValues(prev => {
    const newValues = {
      ...prev,
      [descriptor]: prev[descriptor] === value ? 'none' : value
    };
    
    const filtered = applyFilters(allResults, newValues);
    setSearchResults(filtered);
    
    // 更新特征频率，传入新的过滤器值
    updateOptionsFrequency(allResults, newValues);
    
    return newValues;
  });
}, [allResults, applyFilters, updateOptionsFrequency]);

const handleResetFilter = useCallback((descriptor) => {
  setDescriptorValues(prev => {
    const newValues = {
      ...prev,
      [descriptor]: 'none'
    };
    
    const filtered = applyFilters(allResults, newValues);
    setSearchResults(filtered);
    
    // 更新特征频率，传入新的过滤器值
    updateOptionsFrequency(allResults, newValues);
    
    return newValues;
  });
}, [allResults, applyFilters, updateOptionsFrequency]);

const handleClearFilters = useCallback(() => {
  const newValues = {
    hatColor: 'none',
    hairColor: 'none',
    clothingColor: 'none',
    clothingType: 'none',
    pantsColor: 'none',
    pantsType: 'none',
    shoesColor: 'none'
  };
  
  setDescriptorValues(newValues);
  setSelectedBodyPart(null);
  setSelectedTimeBlocks(['11:00-11:30', '21:30-22:00']);
  setSelectedRobots([]);
  setSelectedConfidenceLevels([]);
  
  setSearchResults(allResults);
  
  // 更新特征频率，传入重置后的过滤器值
  updateOptionsFrequency(allResults, newValues);
}, [allResults, updateOptionsFrequency]);
// 8. Effects
useEffect(() => {
  fetchAllData();
}, [fetchAllData]);

// 9. Define other functions
const handleTimeBlockSelect = useCallback((timeBlock) => {
  const selectableBlocks = ['11:00-11:30', '21:30-22:00'];
  if (!selectableBlocks.includes(timeBlock)) return;
  
  setSelectedTimeBlocks(prev => {
    const newBlocks = prev.includes(timeBlock) 
      ? prev.filter(block => block !== timeBlock) 
      : [...prev, timeBlock];
    
    if (searchResults.length > 0) {
      setTimeout(() => fetchAllData(), 0);
    }
    
    return newBlocks;
  });
}, [searchResults.length, fetchAllData]);

const handleRobotSelect = useCallback((robotNum) => {
  setSelectedRobots(prev => {
    const newRobots = prev.includes(robotNum) 
      ? prev.filter(num => num !== robotNum) 
      : [...prev, robotNum];
    
    if (searchResults.length > 0) {
      setTimeout(() => fetchAllData(), 0);
    }
    
    return newRobots;
  });
}, [searchResults.length, fetchAllData]);

// Add back the selectedDescriptors memoized value
const selectedDescriptors = useMemo(() => {
  const descriptors = [];
  
  if (descriptorValues.hatColor !== 'none') {
    descriptors.push(`Hat: ${capitalizeFirst(descriptorValues.hatColor)}`);
  }
  
  if (descriptorValues.hairColor !== 'none') {
    descriptors.push(`Hair: ${capitalizeFirst(descriptorValues.hairColor)}`);
  }
  
  if (descriptorValues.clothingColor !== 'none' || descriptorValues.clothingType !== 'none') {
    let clothingDesc = 'Clothing:';
    if (descriptorValues.clothingColor !== 'none') {
      clothingDesc += ` ${capitalizeFirst(descriptorValues.clothingColor)}`;
    }
    if (descriptorValues.clothingType !== 'none') {
      clothingDesc += ` ${capitalizeFirst(descriptorValues.clothingType)}`;
    }
    descriptors.push(clothingDesc);
  }
  
  if (descriptorValues.pantsColor !== 'none' || descriptorValues.pantsType !== 'none') {
    let pantsDesc = 'Pants:';
    if (descriptorValues.pantsColor !== 'none') {
      pantsDesc += ` ${capitalizeFirst(descriptorValues.pantsColor)}`;
    }
    if (descriptorValues.pantsType !== 'none') {
      pantsDesc += ` ${capitalizeFirst(descriptorValues.pantsType)}`;
    }
    descriptors.push(pantsDesc);
  }
  
  if (descriptorValues.shoesColor !== 'none') {
    let shoesDesc = 'Shoes:';
    if (descriptorValues.shoesColor !== 'none') {
      shoesDesc += ` ${capitalizeFirst(descriptorValues.shoesColor)}`;
    }
    descriptors.push(shoesDesc);
  }
  
  return descriptors;
}, [descriptorValues, capitalizeFirst]);

// 10. Define renderBarChartFilter after all its dependencies are defined
const renderBarChartFilter = useCallback((descriptor, label) => {
  const frequency = optionsFrequency[descriptor] || {};
  const options = Object.keys(frequency).filter(key => key && key !== 'none').sort();
  
  const maxFrequency = Math.max(...Object.values(frequency), 1);
  
  return (
    <div className="person-tab__filter-item">
      <div className="person-tab__filter-header">
        <h4 className="person-tab__bar-filter-title">{label}</h4>
        {descriptorValues[descriptor] !== 'none' && (
          <button 
            className="person-tab__reset-filter"
            onClick={() => handleResetFilter(descriptor)}
            title="Reset this filter"
          >
            clear
          </button>
        )}
      </div>
      <div className="person-tab__bar-chart">
        {options.map(option => {
          const count = frequency[option] || 0;
          const percentage = Math.max(10, Math.round((count / maxFrequency) * 100));
          const isSelected = descriptorValues[descriptor] === option;
          
          return (
            <div 
              key={option}
              className={`person-tab__bar-item ${isSelected ? 'person-tab__bar-item--selected' : ''}`}
              onClick={() => handleBarSelect(descriptor, option)}
            >
              <div className="person-tab__bar-label">
                {capitalizeFirst(option)}
              </div>
              <div className="person-tab__bar-container">
                <div 
                  className="person-tab__bar-fill"
                  style={{
                    width: `${percentage}%`,
                    backgroundColor: descriptor.includes('Color') ? getColorValue(option) : '#0d6efd'
                  }}
                ></div>
                <span className="person-tab__bar-count">{count}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}, [optionsFrequency, descriptorValues, handleResetFilter, handleBarSelect, getColorValue, capitalizeFirst]);

// 11. Define handleTrackingChange
const handleTrackingChange = useCallback((personId, isTracking) => {
  console.log(`Person ${personId} tracking status changed to: ${isTracking}`);
  
  // 更新全局追踪人物列表
  if (isTracking) {
    setTrackedPersonIds(prev => [...prev, personId]);
  } else {
    setTrackedPersonIds(prev => prev.filter(id => id !== personId));
  }

  // 向后端发送追踪状态更新
  fetch('${API_BASE_URL}/api/tracking', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      personId,
      isTracking,
      timestamp: new Date().toISOString()
    })
  })
  .then(response => {
    if (!response.ok) {
      throw new Error('Network response was not ok');
    }
    return response.json();
  })
  .then(data => {
    console.log('Tracking status updated on backend:', data);
  })
  .catch(error => {
    console.error('Error updating tracking status:', error);
  });
}, []);

// Handle confidence level selection
const handleConfidenceLevelSelect = useCallback((level) => {
  setSelectedConfidenceLevels(prev =>
    prev.includes(level) 
      ? prev.filter(l => l !== level) 
      : [...prev, level]
  );
}, []);

// Handle select change
const handleSelectChange = useCallback((descriptor, value) => {
  setDescriptorValues(prev => ({
    ...prev,
    [descriptor]: value
  }));
  
  // Determine associated body part ID
  let bodyPartId = descriptor.replace(/Color|Type|Feature/g, '');
  
  // When using dropdown, automatically highlight corresponding body part button
  if (value !== 'none') {
    // Only select this part, exclusive selection
    setSelectedBodyPart(bodyPartId);
  } else {
    // Check if the other selector is also none
    const isOtherValueNone = () => {
      if (descriptor.includes('Color')) {
        const otherKey = descriptor.replace(/Color/g, '') + 'Type';
        return !otherKey.includes('hatType') && !otherKey.includes('hairType') && 
               (descriptorValues[otherKey] === 'none' || !descriptorValues[otherKey]);
      } else if (descriptor.includes('Type') || descriptor.includes('Feature')) {
        const otherKey = descriptor.replace(/Type|Feature/g, '') + 'Color';
        return descriptorValues[otherKey] === 'none';
      }
      return true;
    };

    if (isOtherValueNone() && selectedBodyPart === bodyPartId) {
      setSelectedBodyPart(null);
    }
  }
  
  // 自动触发搜索
  fetchAllData();
}, [descriptorValues, selectedBodyPart, fetchAllData]);

// Handle body part selection
const handleBodyPartSelect = useCallback((partId) => {
  // If already selected, deselect it on second click
  if (selectedBodyPart === partId) {
    setSelectedBodyPart(null);
  } else {
    // Otherwise, select only this button (exclusive selection)
    setSelectedBodyPart(partId);
    
    // Set default options for each part
    const colorKey = partId === 'skin' ? 'skinColor' : `${partId}Color`;
    
    // If current selector is none, set a default value
    if (descriptorValues[colorKey] === 'none') {
      handleSelectChange(colorKey, 'none');
    }
    
    // For parts with type, set default value
    if (partId !== 'hat' && partId !== 'hair' && partId !== 'skin') {
      const typeKey = `${partId}Type`;
      if (descriptorValues[typeKey] === 'none') {
        handleSelectChange(typeKey, 'regular');
      }
    }
  }
}, [selectedBodyPart, descriptorValues, handleSelectChange]);

// 修改后的视频播放函数，与VehicleTab保持一致
const handlePlayVideo = useCallback((videoPath, startTime, endTime) => {
  if (!videoPath) {
    console.error('No video path provided');
    return;
  }
  
  // 使用openExternalVideoPlayer函数
  openExternalVideoPlayer(videoPath, startTime, endTime);
  
  console.log(`Opening video: ${videoPath} from ${startTime} to ${endTime}`);
}, []);

// Add this new function to handle navigation to the timeline
const handleGoDetail = useCallback((videoPath, startTime) => {
  if (!videoPath) {
    console.error('No video path provided');
    return;
  }

  // Extract robot ID from video path
  const videoNumberMatch = videoPath.match(/video(\d+)\.mp4/);
  if (!videoNumberMatch) {
    console.error('Could not extract video number from path:', videoPath);
    return;
  }
  
  const robotId = parseInt(videoNumberMatch[1], 10);
  const startTimeSeconds = parseTimeToSeconds(startTime) || 0;
  
  // Import your appStore to update the global state
  const { updateVideoState } = useAppStore.getState();
  
  // Update global state to switch video and navigate to the time
  updateVideoState({
    videoUrl: videoPath,
    robotId: robotId,
    time: startTimeSeconds,
    isPlaying: true,
    currentTime: startTimeSeconds,
    eventId: null,
    lastVideoSwitch: Date.now()
  }, 'timeline_event_click');
  
  console.log(`Go Details: Switch to robot ${robotId}, navigate to ${startTime} (${startTimeSeconds}s)`);
}, []);

// Enhanced formatPersonResult function - properly extract robot number from videoIds with format like "N8_1080p"
const formatPersonResult = (person) => {
  // Process first appearance if exists
  const appearance = person.appearances && person.appearances.length > 0 ? person.appearances[0] : {};
  
  // Extract image path
  const imageUrl = appearance.imagePath || null;
  
  // Extract video information
  const videoId = appearance.videoId || '';
  const videoInfo = videoId 
    ? `${videoId} (${appearance.timestamp || 'N/A'}, ${appearance.time || 'N/A'})` 
    : 'No video info';
  
  // Extract robot/video number from the videoId - handle format like "N8_1080p"
  let robotNumber = null;
  if (videoId) {
    // First try to match format like "N8_1080p" or "D6_1080p"
    const formatMatch = videoId.match(/[DN](\d+)_/);
    if (formatMatch && formatMatch[1]) {
      robotNumber = parseInt(formatMatch[1], 10);
    } else {
      // Fallback to simpler format like "N8" or "D6"
      const simpleMatch = videoId.match(/[DN](\d+)/);
      if (simpleMatch && simpleMatch[1]) {
        robotNumber = parseInt(simpleMatch[1], 10);
      }
    }
  }
  
  // Extract video path and continue with the rest of your existing code...
  let videoPath = appearance.videoSegment?.videoPath || null;
  
  if (!videoPath && videoId) {
    if (videoId.startsWith('N')) {
      const videoNumber = videoId.match(/\d+/)[0];
      videoPath = `/night/videos/video${videoNumber}.mp4`;
    } else if (videoId.startsWith('D')) {
      const videoNumber = videoId.match(/\d+/)[0];
      videoPath = `/day/videos/video${videoNumber}.mp4`;
    } else {
      videoPath = `/videos/${videoId}.mp4`;
    }
  }

  // Extract timestamps
  const startTime = appearance.videoSegment?.startTime || appearance.timestamp || null;
  const endTime = appearance.videoSegment?.endTime || null;
  
  // Convert features to descriptors list
  const features = person.features || {};
  const descriptors = [];
  
  // Process each feature as you did before...
  if (features.hat && features.hat.color) {
    descriptors.push(`Hat: ${capitalizeFirst(features.hat.color)} Hat`);
  }
  
  if (features.hair && features.hair.color) {
    descriptors.push(`Hair: ${capitalizeFirst(features.hair.color)}`);
  }

  if (features.clothing) {
    const clothingDesc = [`Clothing: ${capitalizeFirst(String(features.clothing.color || ''))}`];
    if (features.clothing.type) {
      clothingDesc.push(capitalizeFirst(String(features.clothing.type)));
    }
    descriptors.push(clothingDesc.filter(Boolean).join(' '));
  }
  
  if (features.pants) {
    const pantsDesc = [`Pants: ${capitalizeFirst(features.pants.color || '')}`];
    if (features.pants.type) {
      pantsDesc.push(capitalizeFirst(features.pants.type));
    }
    descriptors.push(pantsDesc.filter(Boolean).join(' '));
  }
  
  if (features.shoes) {
    const shoesDesc = [`Shoes: ${capitalizeFirst(features.shoes.color || '')}`];
    if (features.shoes.type) {
      shoesDesc.push(capitalizeFirst(features.shoes.type));
    }
    descriptors.push(shoesDesc.filter(Boolean).join(' '));
  }
  
  return {
    id: person.id,
    imageUrl: imageUrl,
    descriptors: descriptors,
    videoInfo: videoInfo,
    videoPath: videoPath,      
    videoStartTime: startTime, 
    videoEndTime: endTime,
    rawData: person, // Save complete original data
    // Add these for filtering
    videoId: videoId,
    robotNumber: robotNumber,
    isDayVideo: videoId.startsWith('D'),
    isNightVideo: videoId.startsWith('N'),
    features: {
      hatColor: features.hat?.color || null,
      hairColor: features.hair?.color || null,
      clothingColor: features.clothing?.color || null,
      clothingType: features.clothing?.type || null,
      pantsColor: features.pants?.color || null,
      pantsType: features.pants?.type || null,
      shoesColor: features.shoes?.color || null
    }
  };
};

  // Handle save result
  const handleSaveResult = useCallback((result) => {
    // Implement save logic
    console.log('Saving result:', result);
  }, []);

  // Define body part buttons
  const bodyParts = useMemo(() => [
    { id: 'hat', label: 'Hat', top: '15%', left: '53%' },    // Top of head
    { id: 'hair', label: 'Hair', top: '25%', left: '65%' },  // Side of head
    { id: 'clothing', label: 'Clothing', top: '35%', left: '55%' }, // Right arm
    { id: 'pants', label: 'Pants', top: '70%', left: '67%' }, // Abdomen
    { id: 'shoes', label: 'Shoes', top: '84%', left: '68%' }  // Feet
  ], []);

  // Get button style
  const getBodyButtonStyle = useCallback((partId) => {
    let colorKey = `${partId}Color`;
    let colorValue = descriptorValues[colorKey];
  
    const part = bodyParts.find(p => p.id === partId);
    const baseStyle = {
      top: part.top,
      left: part.left,
      backgroundColor: 'white', // Default white background
      border: '1px solid rgba(0, 0, 0, 0.2)'
    };

    // Only change color if a non-none value is selected
  if (colorValue && colorValue !== 'none') {
    return {
      ...baseStyle,
      backgroundColor: getColorValue(colorValue),
      border: ['black', 'dark', 'blue', 'green', 'brown', 'medium-dark'].includes(colorValue) 
        ? '1px solid white'
        : '1px solid rgba(0, 0, 0, 0.2)'
    };
  }

  return baseStyle;
}, [bodyParts, descriptorValues, getColorValue]);

// Check if part has any options selected
const isPartActive = useCallback((partId) => {
  return descriptorValues[`${partId}Color`] !== 'none' || descriptorValues[`${partId}Type`] !== 'none';
}, [descriptorValues]);

// Loading flag, true if options are loading or searching
const isLoading = optionsLoading || isSearching;

// Function to render tracked people
const renderTrackedPeople = useCallback(() => {
  // 首先检查是否有任何来自搜索结果的追踪人物
  const trackedResults = searchResults.filter(person => 
    trackedPersonIds.includes(person.id)
  );
  
  // 如果有来自props的追踪对象或来自搜索结果的追踪人物，渲染它们
  if ((trackedObjects && trackedObjects.length > 0) || trackedResults.length > 0) {
    return (
      <div className="person-tab__tracked-list">
        {/* 显示来自props的追踪对象 */}
        {trackedObjects.map((person, index) => (
          <div key={`tracked-${index}`} className="person-tab__tracked-item">
            <div className="person-tab__tracked-image">
              {person.imagePath ? (
                <img 
                  src={person.imagePath} 
                  alt={person.label} 
                  className="person-tab__tracked-img"
                  onError={(e) => { e.target.src = "/api/placeholder/100/100?text=Person"; }}
                />
              ) : (
                <div className="person-tab__image-placeholder">No Image</div>
              )}
            </div>
          </div>
        ))}
        
        {/* 显示来自搜索结果的追踪人物 */}
        {trackedResults.map((person) => {
          // 获取第一个 appearance 的图片路径
          const firstAppearance = person.appearances?.[0];
          const imagePath = firstAppearance?.imagePath;
          
          return (
            <div key={`result-${person.id}`} className="person-tab__tracked-item">
              <div className="person-tab__tracked-image">
                {imagePath ? (
                  <img 
                    src={imagePath} 
                    alt={`Person ${person.id}`} 
                    className="person-tab__tracked-img"
                    onError={(e) => { e.target.src = "/api/placeholder/100/100?text=Person"; }}
                  />
                ) : (
                  <div className="person-tab__image-placeholder">No Image</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  }
  
  return (
    <div className="person-tab__no-tracked">
      No people currently being tracked
    </div>
  );
}, [trackedObjects, searchResults, trackedPersonIds]);

const renderTrackerSummary = useCallback(() => {
  if (trackedPersonIds.length === 0) return null;
  
  return (
    <div className="person-tab__tracker-summary">
      <h4 className="person-tab__tracking-subtitle">Tracking Summary</h4>
      <div className="person-tab__tracking-stats">
        <div className="person-tab__tracking-stat">
          <span className="person-tab__tracking-label">Tracked Persons:</span>
          <span className="person-tab__tracking-value">{trackedPersonIds.length}</span>
        </div>
      </div>
    </div>
  );
}, [trackedPersonIds.length]);

return (
  <div className="person-tab">
    <style jsx>{`
      .person-tab__filter-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: 8px;
      }
      
      .person-tab__reset-filter {
        background: none;
        border: none;
        color: #666;
        cursor: pointer;
        padding: 4px;
        font-size: 14px;
        transition: color 0.2s;
      }
      
      .person-tab__reset-filter:hover {
        color: #dc3545;
      }
      
      .person-tab__bar-filter-title {
        margin: 0;
        font-size: 16px;
        font-weight: 600;
        color: #ffffff;
        text-shadow: 1px 1px 2px rgba(0,0,0,0.2);
      }

      .person-tab__filter-title {
        color: #ffffff;
        font-size: 18px;
        margin-bottom: 16px;
      }

      .person-tab__descriptor-list {
        margin-left: 10px;
        color: #ffffff;
      }

      .person-tab__descriptor-item {
        background: rgba(255, 255, 255, 0.1);
        padding: 4px 8px;
        border-radius: 4px;
        margin-right: 8px;
        font-size: 14px;
      }
    `}</style>

    {/* 主要内容区域 */}
    <div className="person-tab__content">
      {/* 筛选区域 */}
      <div className="person-tab__filters">
        <h3 className="person-tab__filter-title">
          Descriptor: {selectedDescriptors.length} chosen
          {selectedDescriptors.length > 0 && (
            <span className="person-tab__descriptor-list">
              {selectedDescriptors.map((desc, index) => (
                <span key={index} className="person-tab__descriptor-item">
                  {desc}
                </span>
              ))}
            </span>
          )}
        </h3>
        
        <div className="person-tab__filter-row">
          {renderBarChartFilter('hatColor', 'Hat Color')}
          {renderBarChartFilter('hairColor', 'Hair Color')}
          {renderBarChartFilter('clothingColor', 'Clothing Color')}
          {renderBarChartFilter('clothingType', 'Clothing Type')}
          {renderBarChartFilter('pantsColor', 'Pants Color')}
          {renderBarChartFilter('pantsType', 'Pants Type')}
          {renderBarChartFilter('shoesColor', 'Shoes Color')}
        </div>

        {/* 搜索按钮区域 */}
        <div className="person-tab__search-actions">
          <button 
            className="person-tab__search-button" 
            onClick={fetchAllData}
            disabled={isLoading}
          >
            {isSearching ? '...' : 'Search'}
          </button>
          <button 
            className="person-tab__clear-button" 
            onClick={handleClearFilters}
            disabled={isLoading}
          >
            Clear
          </button>
        </div>
      </div>
    </div>
    
    {/* 搜索结果区域 */}
    <div className="person-tab__results">
      {isSearching ? (
        <div className="person-tab__loading">Searching...</div>
      ) : searchError ? (
        <div className="person-tab__error">{searchError}</div>
      ) : searchResults.length > 0 ? (
        <div className="person-tab__results-grid">
          {searchResults.map((result) => {
            const uniqueKey = `person-${result.id}-${result.videoId || ''}-${result.timestamp || result.time || Date.now()}`;
            
            return (
              <ResultCard
                key={uniqueKey}
                imageUrl={result.imageUrl}
                descriptors={result.descriptors}
                id={result.id}
                videoInfo={result.videoInfo}
                videoPath={result.videoPath}
                videoStartTime={result.videoStartTime}
                videoEndTime={result.videoEndTime}
                onSave={() => handleSaveResult(result)}
                rawData={result.rawData}
                selectedFilters={descriptorValues}
                onPlayVideo={handlePlayVideo}
                matchCount={result.matchCount}
                matchPercentage={result.matchPercentage}
                videoId={result.videoId}
                isTracked={trackedPersonIds.includes(result.id)}
                onTrackingChange={(isTracking) => handleTrackingChange(result.id, isTracking)}
                onGoDetail={handleGoDetail}
                robotNumber={result.robotNumber}
              />
            );
          })}
        </div>
      ) : (
        <div className="person-tab__no-results">No results found</div>
      )}
    </div>
  </div>
);
};

// Function to get matching and mismatched features
function getMatchingInfo(descriptors, selectedFilters) {
  // If no filters provided or no descriptors, return empty
  if (!selectedFilters || !descriptors || descriptors.length === 0) {
    return { matched: [], mismatched: [] };
  }
  
  // Create array of active filters (excluding items with "none" value)
  const activeFilters = [];
  
  // 处理帽子颜色
  if (selectedFilters.hatColor !== 'none') {
    activeFilters.push(`Hat: ${capitalizeFirst(selectedFilters.hatColor)}`);
  }
  
  // 处理头发颜色
  if (selectedFilters.hairColor !== 'none') {
    activeFilters.push(`Hair: ${capitalizeFirst(selectedFilters.hairColor)}`);
  }
  
  // 处理衣服颜色和类型
  if (selectedFilters.clothingColor !== 'none' || selectedFilters.clothingType !== 'none') {
    let clothingDesc = 'Clothing:';
    if (selectedFilters.clothingColor !== 'none') {
      clothingDesc += ` ${capitalizeFirst(selectedFilters.clothingColor)}`;
    }
    if (selectedFilters.clothingType !== 'none') {
      clothingDesc += ` ${capitalizeFirst(selectedFilters.clothingType)}`;
    }
    activeFilters.push(clothingDesc);
  }
  
  // 处理裤子颜色和类型
  if (selectedFilters.pantsColor !== 'none' || selectedFilters.pantsType !== 'none') {
    let pantsDesc = 'Pants:';
    if (selectedFilters.pantsColor !== 'none') {
      pantsDesc += ` ${capitalizeFirst(selectedFilters.pantsColor)}`;
    }
    if (selectedFilters.pantsType !== 'none') {
      pantsDesc += ` ${capitalizeFirst(selectedFilters.pantsType)}`;
    }
    activeFilters.push(pantsDesc);
  }
  
  // 处理鞋子颜色
  if (selectedFilters.shoesColor !== 'none') {
    let shoesDesc = 'Shoes:';
    if (selectedFilters.shoesColor !== 'none') {
      shoesDesc += ` ${capitalizeFirst(selectedFilters.shoesColor)}`;
    }
    activeFilters.push(shoesDesc);
  }
  
  // If no active filters, return empty
  if (activeFilters.length === 0) {
    return { matched: [], mismatched: [] };
  }

  // Calculate matched and mismatched features
  const matched = [];
  const mismatched = [];
  
  // Check if each active filter is in the descriptors
  activeFilters.forEach(filter => {
    const filterCategory = filter.split(':')[0].trim(); // Extract category (Hat, Hair, etc.)
    const filterDetail = filter.substring(filter.indexOf(':') + 1).trim(); // Extract details
    
    // Find descriptor of the matching category
    const matchingDescriptor = descriptors.find(desc => 
      desc.toLowerCase().startsWith(filterCategory.toLowerCase() + ':')
    );
    
    if (matchingDescriptor) {
      // If descriptor of matching category found, check if details match
      const descriptorDetail = matchingDescriptor.substring(matchingDescriptor.indexOf(':') + 1).trim();
      
      // Special handling for skin features which use comma
      if (filterCategory.toLowerCase() === 'skin' && filterDetail.includes(',')) {
        // Split filter parts and check if each part is in the descriptor
        const filterParts = filterDetail.split(',').map(part => part.trim().toLowerCase());
        const descriptorParts = descriptorDetail.split(',').map(part => part.trim().toLowerCase());
        
        // Check if each filter part is in the descriptor
        const allPartsMatch = filterParts.every(part => 
          descriptorParts.some(descPart => descPart.includes(part))
        );
        
        if (allPartsMatch) {
          matched.push(filter);
        } else {
          mismatched.push(filter);
        }
      } else {
        // For other features, check if all filter words are in the descriptor
        const filterWords = filterDetail.toLowerCase().split(' ').filter(word => word.length > 0);
        const descriptorLower = descriptorDetail.toLowerCase();
        
        // Consider it a match if all words from the filter are in the descriptor
        const allWordsMatch = filterWords.every(word => descriptorLower.includes(word));
        
        if (allWordsMatch) {
          matched.push(filter);
        } else {
          mismatched.push(filter);
        }
      }
    } else {
      // If no matching category descriptor found, it's mismatched
      mismatched.push(filter);
    }
  });
  
  return { matched, mismatched };
}


// ResultCard component - 修改后与 VehicleTab 保持一致
// ResultCard component - Modified with added "Share" button and popup functionality
const ResultCard = ({ 
  imageUrl, 
  descriptors, 
  id, 
  videoInfo, 
  videoPath,
  videoStartTime,
  videoEndTime,
  onSave,
  rawData,
  selectedFilters,
  onPlayVideo,
  matchCount = 0,
  matchPercentage = 0,
  videoId = '',
  robotNumber = null,
  isTracked = false,
  onTrackingChange,
  onGoDetail
}) => {
  const [imageError, setImageError] = useState(false);
  const { addToWorkspace, addSharedEvent } = useSharedEvents();
  const [showEmbeddedVideo, setShowEmbeddedVideo] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [shareMessage, setShareMessage] = useState('');
  const [saveNote, setSaveNote] = useState('');
  const [actionRequired, setActionRequired] = useState('monitor');

  // Get matching info
  const { matched, mismatched } = getMatchingInfo(descriptors, selectedFilters);
  
  // Get border color based on match status
  const getBorderColor = () => {
    if (matched.length === 0) return '#444'; // Default border color
    if (mismatched.length === 0) return '#4caf50'; // Green for full match
    return '#ff9800'; // Orange for partial match
  };

  // Get card class name based on match status
  const getCardClassName = () => {
    let className = "result-card";
    if (matched.length > 0) {
      if (mismatched.length === 0) {
        className += " result-card--match-all";
      } else {
        className += " result-card--match-partial";
      }
    } else {
      className += " result-card--no-match";
    }
    return className;
  };

  // Handle save to workspace
  const handleSaveToWorkspace = () => {
    if (onSave) {
      onSave();
    }
    
    // Parse videoStartTime if it's a string
    let parsedStartTime = videoStartTime;
    if (typeof videoStartTime === 'string' && videoStartTime.includes(':')) {
      // Convert "HH:MM:SS" format to seconds
      const parts = videoStartTime.split(':');
      if (parts.length === 3) {
        parsedStartTime = parseInt(parts[0]) * 3600 + parseInt(parts[1]) * 60 + parseInt(parts[2]);
      } else if (parts.length === 2) {
        parsedStartTime = parseInt(parts[0]) * 60 + parseInt(parts[1]);
      }
    }
    
    // Ensure parsedStartTime is a number
    if (isNaN(parsedStartTime)) {
      parsedStartTime = 0;
    }
    
    const uniqueId = `person-${id}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    
    // Create workspace event object
    const workspaceEvent = {
      id: uniqueId,
      source: 'person',
      type: 'person',
      descriptors: descriptors,
      imageUrl: imageUrl,
      imagePath: imageUrl, // Keep original imageUrl as imagePath
      videoInfo: videoInfo,
      videoPath: videoPath,
      videoStartTime: parsedStartTime,
      videoEndTime: videoEndTime,
      event: `Person ${id}`,
      robotId: robotNumber,
      timeframe: videoId.startsWith('D') ? '11AM' : videoId.startsWith('N') ? '11PM' : null,
      startTime: parsedStartTime,
      savedAt: new Date().toISOString(),
      savedAtDisplay: new Date().toLocaleString(),
      rawData: rawData,
      savedTo: ['workspace'],
      notes: saveNote ? [
        {
          id: Date.now().toString(),
          content: saveNote,
          timestamp: new Date().toISOString(),
          isImportant: false
        }
      ] : []
    };
    
    // Add to workspace
    addToWorkspace(workspaceEvent);
    setShowSaveModal(false);
    setSaveNote('');
  };

  // Handle share to shared space
  const handleShareToShared = () => {
    // Parse videoStartTime if it's a string
    let parsedStartTime = videoStartTime;
    if (typeof videoStartTime === 'string' && videoStartTime.includes(':')) {
      // Convert "HH:MM:SS" format to seconds
      const parts = videoStartTime.split(':');
      if (parts.length === 3) {
        parsedStartTime = parseInt(parts[0]) * 3600 + parseInt(parts[1]) * 60 + parseInt(parts[2]);
      } else if (parts.length === 2) {
        parsedStartTime = parseInt(parts[0]) * 60 + parseInt(parts[1]);
      }
    }
    
    // Ensure parsedStartTime is a number
    if (isNaN(parsedStartTime)) {
      parsedStartTime = 0;
    }
    
    const uniqueId = `person-shared-${id}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    
    // Create the event object to share
    const sharedEvent = {
      id: uniqueId,
      source: 'person',
      type: 'person',
      descriptors: descriptors,
      imageUrl: imageUrl,
      imagePath: imageUrl, // Keep original imageUrl as imagePath
      videoInfo: videoInfo,
      videoPath: videoPath,
      videoStartTime: parsedStartTime,
      videoEndTime: videoEndTime,
      event: `Person ${id}`,
      robotId: robotNumber,
      timeframe: videoId.startsWith('D') ? '11AM' : videoId.startsWith('N') ? '11PM' : null,
      startTime: parsedStartTime,
      savedAt: new Date().toISOString(),
      savedAtDisplay: new Date().toLocaleString(),
      rawData: rawData,
      shareMessage: shareMessage,
      actionRequired: actionRequired,
      priority: actionRequired === 'dispatch' ? 1 : 
             actionRequired === 'investigate' ? 2 : 
             actionRequired === 'monitor' ? 3 : 4,
      messages: [{
        id: Date.now().toString(),
        sender: "Current Officer",
        message: shareMessage,
        timestamp: new Date().toISOString(),
      }],
      savedTo: ['shared']
    };
    
    // Add to shared events
    addSharedEvent(sharedEvent);
    
    // Close the modal
    setShowShareModal(false);
    setShareMessage('');
    setActionRequired('monitor');
  };

  // Handle video play
  const handlePlayClick = () => {
    setShowEmbeddedVideo(prev => !prev);
  };

  // Extract video type for display
  const videoType = videoId.startsWith('D') ? 'Day' : videoId.startsWith('N') ? 'Night' : '';

  return (
    <div className={getCardClassName()} style={{
      border: `2px solid ${getBorderColor()}`,
      boxShadow: matched.length > 0 ? `0 2px 8px rgba(${mismatched.length === 0 ? '76, 175, 80' : '255, 152, 0'}, 0.3)` : 'none',
      position: 'relative'
    }}>
      {/* Robot number badge */}
      {robotNumber && (
        <div className="result-card__robot-badge" style={{
          position: 'absolute',
          top: '10px',
          right: '10px',
          background: videoId.startsWith('D') ? '#2196F3' : '#673AB7',
          color: 'white',
          borderRadius: '50%',
          width: '30px',
          height: '30px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontWeight: 'bold',
          fontSize: '14px',
          boxShadow: '0 2px 5px rgba(0,0,0,0.2)'
        }}>
          {robotNumber}
        </div>
      )}
      
      {/* Left side image area */}
      <div className="result-card__image">
        <div className="result-card__object-image">
          {imageUrl ? (
            <img 
              src={imageUrl} 
              alt={`Person ${id}`} 
              className="result-card__img"
              onError={(e) => {
                console.error(`Image load failed: ${imageUrl}`);
                // Try backup method
                const fileName = imageUrl.split('/').pop();
                e.target.src = `${API_BASE_URL}/img/${fileName}`;
              }}
            />
          ) : (
            <div className="result-card__video-placeholder">
              No image available
            </div>
          )}
        </div>
      </div>
      
      {/* Right side details area */}
      <div className="result-card__details">
        {/* Add explicit video type info at the top */}
        <div className="result-card__video-type" style={{
          background: videoType === 'Day' ? '#90caf9' : '#7986cb',
          color: '#fff',
          padding: '2px 6px',
          borderRadius: '3px',
          display: 'inline-block',
          fontSize: '12px',
          marginBottom: '8px',
          fontWeight: 'bold'
        }}>
          {videoType} Video {robotNumber ? `#${robotNumber}` : ''}
        </div>
        
        {/* Descriptor matching info */}
        <div className="result-card__descriptor-info">
          <h3 className="result-card__subtitle">Descriptor Matching</h3>
          {matched.length > 0 && (
            <div className="result-card__desc-line">
              <span className="result-card__desc-label">Match:</span>
              <span className="result-card__desc-value result-card__desc-value--match">
                {matched.join(', ')}
              </span>
            </div>
          )}
          
          {mismatched.length > 0 && (
            <div className="result-card__desc-line">
              <span className="result-card__desc-label">Mis:</span>
              <span className="result-card__desc-value result-card__desc-value--mismatch">
                {mismatched.join(', ')}
              </span>
            </div>
          )}
          {/* Display all features */}
          <div className="result-card__desc-line">
            <span className="result-card__desc-label">Features:</span>
            <span className="result-card__desc-value">
              {/* Highlight features */}
              {descriptors.map((desc, index) => {
                const category = desc.split(':')[0].trim();
                const isMatched = matched.some(m => m.startsWith(category + ':'));
                const isMismatched = mismatched.some(m => m.startsWith(category + ':'));
                
                return (
                  <span key={index} className={`result-card__feature-text ${isMatched ? 'result-card__feature-text--match' : (isMismatched ? 'result-card__feature-text--mismatch' : '')}`}>
                    {desc}{index < descriptors.length - 1 ? ', ' : ''}
                  </span>
                );
              })}
            </span>
          </div>
        </div>
        
        {/* Video info area */}
        {videoInfo && (
          <div className="result-card__video-info">
            <h3 className="result-card__subtitle">Detected Location</h3>
            <p className="result-card__video-text">{videoInfo}</p>
            
            {/* Embedded video player */}
            {showEmbeddedVideo && (
              <EnhancedVideoPlayer
                videoPath={videoPath}
                startTime={videoStartTime}
                onClose={() => setShowEmbeddedVideo(false)}
                onGoDetail={onGoDetail}
              />
            )}
            
            {/* Button container - with Save and Share buttons */}
            <div className="result-card__button-container">
              {/* Play Video button */}
              <button 
                className="result-card__video-button"
                onClick={handlePlayClick}
              >
                <span style={{ marginRight: '5px' }}>{showEmbeddedVideo ? '🛑' : '🎬'}</span>
                {showEmbeddedVideo ? 'Close Video' : 'Play Video'}
              </button>
              
              {/* Save button - Opens save modal */}
              <button 
                className="result-card__save-button" 
                onClick={() => setShowSaveModal(true)}
              >
                <span style={{ marginRight: '5px' }}>💾</span>
                Save
              </button>
              
              {/* Share button - Opens share modal */}
              <button 
                className="result-card__share-button" 
                onClick={() => setShowShareModal(true)}
              >
                <span style={{ marginRight: '5px' }}>📤</span>
                Share
              </button>
            </div>
          </div>
        )}
      </div>
      
      {/* Save Modal */}
      {showSaveModal && (
        <div className="modal-overlay" onClick={() => setShowSaveModal(false)}>
          <div className="modal-container" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Add to Workspace</h3>
              <button className="modal-close" onClick={() => setShowSaveModal(false)}>
                ×
              </button>
            </div>
            
            <div className="modal-content">
              {/* Event preview */}
              <div className="modal-event-preview">
                <div className="preview-image" style={{ height: '100%' }}>
                  <img 
                    src={imageUrl} 
                    alt={`Person ${id}`}
                    style={{ height: '100%', objectFit: 'cover' }}
                    onError={(e) => {
                      console.error(`Image load failed: ${imageUrl}`);
                      // Try backup method
                      const fileName = imageUrl.split('/').pop();
                      e.target.src = `${API_BASE_URL}/img/${fileName}`;
                    }}
                  />
                </div>
                <div className="preview-info">
                  <h4>Person {id}</h4>
                  <div className="preview-details">
                    {descriptors.length > 0 && (
                      <span className="preview-type">{descriptors[0]}</span>
                    )}
                    <span className="preview-time">{videoInfo}</span>
                    <span className="preview-unit">Unit {robotNumber}</span>
                  </div>
                </div>
              </div>
              
              {/* Note form */}
              <div className="modal-form">
                <div className="form-group">
                  <label>Add Notes</label>
                  <textarea
                    value={saveNote}
                    onChange={e => setSaveNote(e.target.value)}
                    placeholder="Add details about this evidence..."
                    rows={4}
                  />
                </div>
              </div>
            </div>
            
            <div className="modal-footer">
              <button className="modal-btn cancel" onClick={() => setShowSaveModal(false)}>
                Cancel
              </button>
              <button 
                className="modal-btn save" 
                onClick={handleSaveToWorkspace}
              >
                <span style={{ marginRight: '5px' }}>💾</span>
                Save to Workspace
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Share Modal */}
      {showShareModal && (
        <div className="modal-overlay" onClick={() => setShowShareModal(false)}>
          <div className="modal-container" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Share with Team</h3>
              <button className="modal-close" onClick={() => setShowShareModal(false)}>
                ×
              </button>
            </div>
            
            <div className="modal-content">
              {/* Event preview */}
              <div className="modal-event-preview">
                <div className="preview-image" style={{ height: '100%' }}>
                  <img 
                    src={imageUrl} 
                    alt={`Person ${id}`}
                    style={{ height: '100%', objectFit: 'cover' }}
                    onError={(e) => {
                      console.error(`Image load failed: ${imageUrl}`);
                      // Try backup method
                      const fileName = imageUrl.split('/').pop();
                      e.target.src = `${API_BASE_URL}/img/${fileName}`;
                    }}
                  />
                </div>
                <div className="preview-info">
                  <h4>Person {id}</h4>
                  <div className="preview-details">
                    {descriptors.length > 0 && (
                      <span className="preview-type">{descriptors[0]}</span>
                    )}
                    <span className="preview-time">{videoInfo}</span>
                    <span className="preview-unit">Unit {robotNumber}</span>
                  </div>
                </div>
              </div>
              
              {/* Message form */}
              <div className="modal-form">
                <div className="form-group">
                  <label>Message for Team</label>
                  <textarea
                    value={shareMessage}
                    onChange={e => setShareMessage(e.target.value)}
                    placeholder="Add context or instructions for your team..."
                    rows={4}
                  />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Action Required</label>
                    <select 
                      value={actionRequired}
                      onChange={(e) => setActionRequired(e.target.value)}
                    >
                      <option value="dispatch">Immediate Dispatch</option>
                      <option value="investigate">Investigate ASAP</option>
                      <option value="monitor">Monitor & Update</option>
                      <option value="reference">For Reference Only</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="modal-footer">
              <button className="modal-btn cancel" onClick={() => setShowShareModal(false)}>
                Cancel
              </button>
              <button 
                className="modal-btn share" 
                onClick={handleShareToShared}
                disabled={!shareMessage.trim()}
              >
                <span style={{ marginRight: '5px' }}>📤</span>
                Share with Team
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
export default PersonTab;



